import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Users, TrendingUp, Gift, Plus, User, LogOut, Search, Euro, Edit2, Trash2, 
  Download, QrCode as QrCodeIcon, AlertTriangle, Award, CreditCard, Palette, 
  Key, RefreshCw, Zap, Settings, Clock, Sparkles, ChevronRight, X, Eye, Coins,
  ShoppingBag, Store, SlidersHorizontal, History, Menu, XCircle, Send
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useTheme } from '@/context/ThemeContext';
import ThemeToggle from '@/components/ThemeToggle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});


const PDF_CP1252 = {
  0x20AC: 0x80, 0x201A: 0x82, 0x0192: 0x83, 0x201E: 0x84,
  0x2026: 0x85, 0x2020: 0x86, 0x2021: 0x87, 0x02C6: 0x88,
  0x2030: 0x89, 0x0160: 0x8A, 0x2039: 0x8B, 0x0152: 0x8C,
  0x017D: 0x8E, 0x2018: 0x91, 0x2019: 0x92, 0x201C: 0x93,
  0x201D: 0x94, 0x2022: 0x95, 0x2013: 0x96, 0x2014: 0x97,
  0x02DC: 0x98, 0x2122: 0x99, 0x0161: 0x9A, 0x203A: 0x9B,
  0x0153: 0x9C, 0x017E: 0x9E, 0x0178: 0x9F
};

const encodePdfBytes = (value) => {
  const source = String(value ?? '');
  const bytes = [];
  for (const char of source) {
    const code = char.codePointAt(0);
    if (code <= 0xFF) bytes.push(code);
    else if (PDF_CP1252[code]) bytes.push(PDF_CP1252[code]);
    else bytes.push(0x3F);
  }
  return new Uint8Array(bytes);
};

const concatPdfBytes = (parts) => {
  const totalLength = parts.reduce((total, part) => total + part.length, 0);
  const output = new Uint8Array(totalLength);
  let offset = 0;
  parts.forEach(part => {
    output.set(part, offset);
    offset += part.length;
  });
  return output;
};

const cleanPdfText = (value) => String(value ?? '')
  .replace(/[\r\n\t]+/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const escapePdfText = (value) => cleanPdfText(value)
  .replace(/\\/g, '\\\\')
  .replace(/\(/g, '\\(')
  .replace(/\)/g, '\\)');

const shortenPdfText = (value, maxLength) => {
  const clean = cleanPdfText(value);
  if (clean.length <= maxLength) return clean;
  return `${clean.slice(0, Math.max(1, maxLength - 3)).trim()}...`;
};

const pdfNumber = (value) => Number(value || 0).toLocaleString('it-IT');

const buildCustomerPointsPdf = (customers, cards, settings) => {
  const PAGE_WIDTH = 595;
  const PAGE_HEIGHT = 842;
  const MARGIN = 34;
  const TABLE_WIDTH = PAGE_WIDTH - (MARGIN * 2);
  const ROW_HEIGHT = 42;
  const HEADER_HEIGHT = 28;
  const BOTTOM_LIMIT = 54;
  const POINTS_FOR_DISCOUNT = Math.max(1, Number(settings?.points_for_discount || 10));
  const DISCOUNT_VALUE = Number(settings?.discount_value || 5);
  const cardsByUser = new Map(cards.map(card => [String(card.user_id), card]));

  const rows = customers
    .map(customer => {
      const card = cardsByUser.get(String(customer.id)) || {};
      return {
        name: customer.full_name || 'Cliente senza nome',
        email: customer.email || 'Senza email',
        phone: customer.phone || 'Telefono non indicato',
        createdAt: customer.created_at ? new Date(customer.created_at).toLocaleDateString('it-IT') : '-',
        currentPoints: Number(card.current_points || 0),
        totalPoints: Number(card.total_points_earned || 0)
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name, 'it', { sensitivity: 'base' }));

  const totalCurrentPoints = rows.reduce((sum, row) => sum + row.currentPoints, 0);
  const totalEarnedPoints = rows.reduce((sum, row) => sum + row.totalPoints, 0);
  const readyDiscounts = rows.reduce(
    (sum, row) => sum + Math.floor(row.currentPoints / POINTS_FOR_DISCOUNT),
    0
  );
  const generatedAt = new Date();
  const generatedDate = generatedAt.toLocaleDateString('it-IT');
  const generatedTime = generatedAt.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });

  const pages = [];
  let pageCommands = [];
  let cursorY = 0;
  let pageNumber = 0;

  const addCommand = (command) => pageCommands.push(command);
  const setFill = (r, g, b) => addCommand(`${r} ${g} ${b} rg`);
  const setStroke = (r, g, b) => addCommand(`${r} ${g} ${b} RG`);
  const fillRect = (x, y, width, height, color) => {
    setFill(...color);
    addCommand(`${x} ${y} ${width} ${height} re f`);
  };
  const strokeRect = (x, y, width, height, color, lineWidth = 0.5) => {
    setStroke(...color);
    addCommand(`${lineWidth} w ${x} ${y} ${width} ${height} re S`);
  };
  const drawLine = (x1, y1, x2, y2, color, lineWidth = 0.5) => {
    setStroke(...color);
    addCommand(`${lineWidth} w ${x1} ${y1} m ${x2} ${y2} l S`);
  };
  const drawText = (value, x, y, size = 9, font = 'F1', color = [0.12, 0.12, 0.12]) => {
    const safe = escapePdfText(value);
    addCommand(`BT /${font} ${size} Tf ${color.join(' ')} rg 1 0 0 1 ${x} ${y} Tm (${safe}) Tj ET`);
  };
  const drawCenteredText = (value, centerX, y, size = 9, font = 'F1', color = [0.12, 0.12, 0.12]) => {
    const clean = cleanPdfText(value);
    const estimatedWidth = clean.length * size * 0.49;
    drawText(clean, centerX - (estimatedWidth / 2), y, size, font, color);
  };

  const drawPageHeader = (isFirstPage) => {
    pageNumber += 1;
    pageCommands = [];

    fillRect(0, PAGE_HEIGHT - 72, PAGE_WIDTH, 72, [0.36, 0.09, 0.13]);
    fillRect(MARGIN, PAGE_HEIGHT - 18, 54, 4, [0.77, 0.63, 0.35]);
    drawText('MACELLERIA BORGESE', MARGIN, PAGE_HEIGHT - 40, 19, 'F2', [1, 1, 1]);
    drawText('Report Fidelity Card - punti clienti', MARGIN, PAGE_HEIGHT - 59, 10, 'F1', [0.93, 0.87, 0.73]);
    drawText(`Generato il ${generatedDate} alle ${generatedTime}`, PAGE_WIDTH - 196, PAGE_HEIGHT - 42, 8, 'F1', [1, 1, 1]);

    if (isFirstPage) {
      const boxTop = PAGE_HEIGHT - 92;
      const gap = 8;
      const boxWidth = (TABLE_WIDTH - (gap * 3)) / 4;
      const summaries = [
        ['CLIENTI', pdfNumber(rows.length)],
        ['PUNTI ATTUALI', pdfNumber(totalCurrentPoints)],
        ['PUNTI TOTALI', pdfNumber(totalEarnedPoints)],
        ['SCONTI PRONTI', pdfNumber(readyDiscounts)]
      ];
      summaries.forEach(([label, value], index) => {
        const x = MARGIN + index * (boxWidth + gap);
        const y = boxTop - 52;
        fillRect(x, y, boxWidth, 52, [0.97, 0.96, 0.94]);
        strokeRect(x, y, boxWidth, 52, [0.86, 0.82, 0.73], 0.6);
        drawText(label, x + 9, y + 34, 7, 'F2', [0.36, 0.09, 0.13]);
        drawText(value, x + 9, y + 13, 16, 'F2', [0.17, 0.17, 0.17]);
      });

      drawText(
        `Regola attiva: ${POINTS_FOR_DISCOUNT} punti = €${DISCOUNT_VALUE.toFixed(2)} di sconto`,
        MARGIN,
        PAGE_HEIGHT - 164,
        9,
        'F2',
        [0.36, 0.09, 0.13]
      );
      cursorY = PAGE_HEIGHT - 186;
    } else {
      cursorY = PAGE_HEIGHT - 94;
    }

    fillRect(MARGIN, cursorY - HEADER_HEIGHT, TABLE_WIDTH, HEADER_HEIGHT, [0.36, 0.09, 0.13]);
    const col1 = MARGIN;
    const col2 = MARGIN + 190;
    const col3 = col2 + 190;
    const col4 = col3 + 70;
    const col5 = MARGIN + TABLE_WIDTH;
    drawText('CLIENTE', col1 + 8, cursorY - 18, 8, 'F2', [1, 1, 1]);
    drawText('CONTATTI', col2 + 8, cursorY - 18, 8, 'F2', [1, 1, 1]);
    drawCenteredText('PUNTI ATT.', (col3 + col4) / 2, cursorY - 18, 7, 'F2', [1, 1, 1]);
    drawCenteredText('PUNTI TOT.', (col4 + col5) / 2, cursorY - 18, 7, 'F2', [1, 1, 1]);
    cursorY -= HEADER_HEIGHT;
  };

  const finishPage = () => {
    drawLine(MARGIN, 40, PAGE_WIDTH - MARGIN, 40, [0.82, 0.82, 0.82], 0.5);
    drawText(`Macelleria Borgese - Fidelity Card`, MARGIN, 25, 7, 'F1', [0.38, 0.38, 0.38]);
    drawText(`Pagina ${pageNumber}`, PAGE_WIDTH - MARGIN - 42, 25, 7, 'F1', [0.38, 0.38, 0.38]);
    pages.push(pageCommands.join('\n'));
  };

  drawPageHeader(true);

  if (rows.length === 0) {
    fillRect(MARGIN, cursorY - 70, TABLE_WIDTH, 70, [0.98, 0.98, 0.98]);
    strokeRect(MARGIN, cursorY - 70, TABLE_WIDTH, 70, [0.86, 0.86, 0.86], 0.6);
    drawCenteredText('Nessun cliente presente nel sistema.', PAGE_WIDTH / 2, cursorY - 40, 11, 'F2', [0.36, 0.09, 0.13]);
  } else {
    rows.forEach((row, index) => {
      if (cursorY - ROW_HEIGHT < BOTTOM_LIMIT) {
        finishPage();
        drawPageHeader(false);
      }

      const rowBottom = cursorY - ROW_HEIGHT;
      if (index % 2 === 0) fillRect(MARGIN, rowBottom, TABLE_WIDTH, ROW_HEIGHT, [0.985, 0.98, 0.97]);
      strokeRect(MARGIN, rowBottom, TABLE_WIDTH, ROW_HEIGHT, [0.87, 0.87, 0.87], 0.35);

      const col1 = MARGIN;
      const col2 = MARGIN + 190;
      const col3 = col2 + 190;
      const col4 = col3 + 70;
      const col5 = MARGIN + TABLE_WIDTH;
      drawLine(col2, rowBottom, col2, cursorY, [0.9, 0.9, 0.9], 0.35);
      drawLine(col3, rowBottom, col3, cursorY, [0.9, 0.9, 0.9], 0.35);
      drawLine(col4, rowBottom, col4, cursorY, [0.9, 0.9, 0.9], 0.35);

      drawText(shortenPdfText(row.name, 31), col1 + 8, cursorY - 16, 9, 'F2', [0.16, 0.16, 0.16]);
      drawText(`Iscritto: ${row.createdAt}`, col1 + 8, cursorY - 30, 7, 'F1', [0.45, 0.45, 0.45]);
      drawText(shortenPdfText(row.email, 31), col2 + 8, cursorY - 15, 8, 'F1', [0.2, 0.2, 0.2]);
      drawText(shortenPdfText(row.phone, 24), col2 + 8, cursorY - 29, 8, 'F1', [0.45, 0.45, 0.45]);
      drawCenteredText(pdfNumber(row.currentPoints), (col3 + col4) / 2, cursorY - 25, 11, 'F2', [0.36, 0.09, 0.13]);
      drawCenteredText(pdfNumber(row.totalPoints), (col4 + col5) / 2, cursorY - 25, 10, 'F2', [0.28, 0.28, 0.28]);

      cursorY = rowBottom;
    });
  }

  finishPage();

  const objects = [];
  const pageIds = pages.map((_, index) => 5 + (index * 2));
  objects[1] = encodePdfBytes('<< /Type /Catalog /Pages 2 0 R >>');
  objects[2] = encodePdfBytes(`<< /Type /Pages /Kids [${pageIds.map(id => `${id} 0 R`).join(' ')}] /Count ${pages.length} >>`);
  objects[3] = encodePdfBytes('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>');
  objects[4] = encodePdfBytes('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>');

  pages.forEach((commands, index) => {
    const pageId = 5 + (index * 2);
    const contentId = pageId + 1;
    const streamBytes = encodePdfBytes(commands);
    objects[pageId] = encodePdfBytes(
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${PAGE_WIDTH} ${PAGE_HEIGHT}] /Resources << /Font << /F1 3 0 R /F2 4 0 R >> >> /Contents ${contentId} 0 R >>`
    );
    objects[contentId] = concatPdfBytes([
      encodePdfBytes(`<< /Length ${streamBytes.length} >>\nstream\n`),
      streamBytes,
      encodePdfBytes('\nendstream')
    ]);
  });

  const headerBytes = concatPdfBytes([
    encodePdfBytes('%PDF-1.4\n%'),
    new Uint8Array([0xE2, 0xE3, 0xCF, 0xD3]),
    encodePdfBytes('\n')
  ]);
  const outputParts = [headerBytes];
  const offsets = [0];
  let currentOffset = headerBytes.length;

  for (let objectId = 1; objectId < objects.length; objectId += 1) {
    offsets[objectId] = currentOffset;
    const objectBytes = concatPdfBytes([
      encodePdfBytes(`${objectId} 0 obj\n`),
      objects[objectId],
      encodePdfBytes('\nendobj\n')
    ]);
    outputParts.push(objectBytes);
    currentOffset += objectBytes.length;
  }

  const xrefOffset = currentOffset;
  let xref = `xref\n0 ${objects.length}\n0000000000 65535 f \n`;
  for (let objectId = 1; objectId < objects.length; objectId += 1) {
    xref += `${String(offsets[objectId]).padStart(10, '0')} 00000 n \n`;
  }
  xref += `trailer\n<< /Size ${objects.length} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;
  outputParts.push(encodePdfBytes(xref));

  return new Blob([concatPdfBytes(outputParts)], { type: 'application/pdf' });
};

export default function AdminDashboard({ user, onLogout }) {
  const can = (...items) => user?.role === 'admin' || items.some(item => (user?.permissions || []).includes(item));
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loyaltySettings, setLoyaltySettings] = useState({ points_for_discount: 10, discount_value: 5 });
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerCard, setCustomerCard] = useState(null);
  const [customerHistory, setCustomerHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAdminMenu, setShowAdminMenu] = useState(false);
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [showViewCardModal, setShowViewCardModal] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [resetPasswordResult, setResetPasswordResult] = useState(null);
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [showResetPointsModal, setShowResetPointsModal] = useState(false);
  const [resetPointsPassword, setResetPointsPassword] = useState('');
  const [resetPointsLoading, setResetPointsLoading] = useState(false);
  const [showEmailBlockModal, setShowEmailBlockModal] = useState(false);
  const [emailBlockPassword, setEmailBlockPassword] = useState('');
  const [emailBlockReason, setEmailBlockReason] = useState('');
  const [emailBlockLoading, setEmailBlockLoading] = useState(false);
  const [redeemPointsLoading, setRedeemPointsLoading] = useState(false);
  const [refreshingCustomerId, setRefreshingCustomerId] = useState(null);
  const [sendingPointsReport, setSendingPointsReport] = useState(false);
  
  // Form states
  const [purchaseAmount, setPurchaseAmount] = useState('');
  const [newCustomerData, setNewCustomerData] = useState({ full_name: '', email: '', phone: '', address: '', initial_points: '', admin_managed_only: false });
  const [createdCustomerInfo, setCreatedCustomerInfo] = useState(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [customersRes, statsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/admin/customers`, getAuthHeaders()),
        axios.get(`${API}/admin/stats`, getAuthHeaders()),
        axios.get(`${API}/settings`)
      ]);
      setCustomers(customersRes.data);
      setStats(statsRes.data);
      setLoyaltySettings(settingsRes.data || { points_for_discount: 10, discount_value: 5 });
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCustomer = async (customer) => {
    setSelectedCustomer(customer);
    setCustomerHistory([]);
    setHistoryLoading(true);
    try {
      const [cardRes, historyRes] = await Promise.all([
        axios.get(`${API}/cards/${customer.id}`, getAuthHeaders()),
        axios.get(`${API}/transactions/${customer.id}`, getAuthHeaders()).catch(() => ({ data: [] }))
      ]);
      setCustomerCard(cardRes.data);
      setCustomerHistory(historyRes.data || []);
    } catch (error) {
      toast.error('Errore nel caricamento della carta');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleAddPurchase = async () => {
    if (!selectedCustomer || !purchaseAmount) return;
    
    setProcessing(true);
    try {
      const response = await axios.post(
        `${API}/admin/add-purchase`,
        { user_id: selectedCustomer.id, amount: parseFloat(purchaseAmount) },
        getAuthHeaders()
      );
      toast.success(response.data.message || `+${response.data.points_earned} punti aggiunti!`);
      setShowPurchaseModal(false);
      setPurchaseAmount('');
      handleSelectCustomer(selectedCustomer);
      fetchData();
    } catch (error) {
      toast.error('Errore nella registrazione');
    } finally {
      setProcessing(false);
    }
  };

  const handleCreateCustomer = async () => {
    if (!newCustomerData.full_name.trim()) {
      toast.error('Inserisci il nome del cliente');
      return;
    }
    if (newCustomerData.admin_managed_only && !newCustomerData.phone.trim()) {
      toast.error('Per un cliente senza email il telefono è obbligatorio');
      return;
    }
    if (!newCustomerData.admin_managed_only && !newCustomerData.email.trim()) {
      toast.error('Inserisci l’email oppure seleziona cliente senza email');
      return;
    }
    
    setProcessing(true);
    try {
      const payload = {
        ...newCustomerData,
        email: newCustomerData.admin_managed_only ? null : newCustomerData.email.trim(),
        initial_points: Math.max(0, parseInt(newCustomerData.initial_points || '0', 10) || 0),
      };
      const response = await axios.post(`${API}/admin/create-customer`, payload, getAuthHeaders());
      setCreatedCustomerInfo(response.data);
      toast.success(response.data?.message || 'Cliente creato!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    } finally {
      setProcessing(false);
    }
  };

  const handleResetPassword = async () => {
    if (!selectedCustomer) return;
    
    setResetPasswordLoading(true);
    try {
      const response = await axios.post(
        `${API}/admin/reset-password/${selectedCustomer.id}`,
        {},
        getAuthHeaders()
      );
      setResetPasswordResult(response.data);
      toast.success(response.data?.message || 'Password resettata!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel reset password');
      setShowResetPasswordModal(false);
    } finally {
      setResetPasswordLoading(false);
    }
  };

  const handleResetCustomerPoints = async () => {
    if (!selectedCustomer || !resetPointsPassword) {
      toast.error('Inserisci la password amministratore');
      return;
    }

    setResetPointsLoading(true);
    try {
      const response = await axios.post(
        `${API}/admin/reset-customer-points/${selectedCustomer.id}`,
        { password: resetPointsPassword },
        getAuthHeaders()
      );
      toast.success(response.data?.message || 'Punti del cliente azzerati');
      setShowResetPointsModal(false);
      setResetPointsPassword('');
      await handleSelectCustomer(selectedCustomer);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'azzeramento dei punti');
    } finally {
      setResetPointsLoading(false);
    }
  };

  const handleRedeemStandardDiscount = async () => {
    if (!selectedCustomer || !customerCard) return;
    const pointsForDiscount = Number(loyaltySettings?.points_for_discount || 10);
    const discountValue = Number(loyaltySettings?.discount_value || 5);
    if (Number(customerCard.current_points || 0) < pointsForDiscount) {
      toast.error(`Il cliente non ha ancora ${pointsForDiscount} punti`);
      return;
    }

    const confirmed = window.confirm(
      `Applicare €${discountValue.toFixed(2)} di sconto a ${selectedCustomer.full_name} e togliere ${pointsForDiscount} punti?`
    );
    if (!confirmed) return;

    setRedeemPointsLoading(true);
    try {
      const response = await axios.post(
        `${API}/admin/redeem-standard-discount/${selectedCustomer.id}`,
        {},
        getAuthHeaders()
      );
      toast.success(response.data?.message || 'Sconto applicato');
      await handleSelectCustomer(selectedCustomer);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante lo sconto dei punti');
    } finally {
      setRedeemPointsLoading(false);
    }
  };

  const closeEmailBlockModal = () => {
    setShowEmailBlockModal(false);
    setEmailBlockPassword('');
    setEmailBlockReason('');
  };

  const handleToggleEmailBlock = async () => {
    if (!selectedCustomer || selectedCustomer.no_email_account || selectedCustomer.admin_managed_only || !selectedCustomer.email) {
      toast.error('Questo cliente non possiede un indirizzo email bloccabile');
      return;
    }
    if (!emailBlockPassword) {
      toast.error('Inserisci la password amministratore');
      return;
    }

    const wasBlocked = Boolean(selectedCustomer.blocked);
    setEmailBlockLoading(true);
    try {
      const payload = {
        email: selectedCustomer.email,
        password: emailBlockPassword,
      };
      if (!wasBlocked && emailBlockReason.trim()) {
        payload.reason = emailBlockReason.trim();
      }

      const response = await axios.post(
        `${API}/admin/blocked-emails/${wasBlocked ? 'unblock' : 'block'}`,
        payload,
        getAuthHeaders()
      );

      const customersRes = await axios.get(`${API}/admin/customers`, getAuthHeaders());
      const updatedCustomers = customersRes.data || [];
      const updatedCustomer = updatedCustomers.find(customer => customer.id === selectedCustomer.id)
        || { ...selectedCustomer, blocked: !wasBlocked };

      setCustomers(updatedCustomers);
      setSelectedCustomer(updatedCustomer);
      toast.success(response.data?.message || (wasBlocked ? 'Email sbloccata' : 'Email bloccata'));
      closeEmailBlockModal();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il blocco dell’email');
    } finally {
      setEmailBlockLoading(false);
    }
  };

  const handleDeleteCustomer = async () => {
    if (!selectedCustomer) return;
    
    try {
      await axios.delete(`${API}/admin/delete-customer/${selectedCustomer.id}`, getAuthHeaders());
      toast.success('Cliente eliminato');
      setShowDeleteModal(false);
      setSelectedCustomer(null);
      setCustomerCard(null);
      setCustomerHistory([]);
      fetchData();
    } catch (error) {
      console.error('Error deleting customer:', error);
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
    }
  };

  const handleExportData = async () => {
    try {
      const response = await axios.get(`${API}/admin/export-data`, getAuthHeaders());
      const dataStr = JSON.stringify(response.data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `borgese-backup-${new Date().toISOString().split('T')[0]}.json`;
      link.click();
      toast.success('Dati esportati!');
    } catch (error) {
      toast.error('Errore esportazione');
    }
  };

  const handleExportCustomerPoints = async () => {
    try {
      const response = await axios.get(`${API}/admin/export-data`, getAuthHeaders());
      const exportedCustomers = Array.isArray(response.data?.customers) ? response.data.customers : [];
      const exportedCards = Array.isArray(response.data?.cards) ? response.data.cards : [];
      const pdfBlob = buildCustomerPointsPdf(exportedCustomers, exportedCards, loyaltySettings);
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `punti-clienti-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      toast.success(`PDF creato con ${exportedCustomers.length} clienti`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante la creazione del PDF punti');
    }
  };

  const handleSendCustomerPointsToTelegram = async () => {
    if (sendingPointsReport) return;
    const confirmed = window.confirm(
      'Inviare adesso su Telegram il PDF aggiornato con i punti di tutti i clienti?'
    );
    if (!confirmed) return;

    setSendingPointsReport(true);
    try {
      const response = await axios.post(
        `${API}/admin/points-report/telegram`,
        {},
        getAuthHeaders()
      );
      toast.success(response.data?.message || 'PDF punti inviato su Telegram');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l’invio del PDF su Telegram');
    } finally {
      setSendingPointsReport(false);
    }
  };

  const handleRefreshCustomerCard = async (customer, event) => {
    event?.stopPropagation();
    if (!customer?.id || refreshingCustomerId) return;

    setRefreshingCustomerId(customer.id);
    try {
      const [customersRes, statsRes, cardRes, historyRes] = await Promise.all([
        axios.get(`${API}/admin/customers`, getAuthHeaders()),
        axios.get(`${API}/admin/stats`, getAuthHeaders()),
        axios.get(`${API}/cards/${customer.id}`, getAuthHeaders()),
        axios.get(`${API}/transactions/${customer.id}`, getAuthHeaders()).catch(() => ({ data: [] }))
      ]);

      const updatedCustomers = customersRes.data || [];
      const updatedCustomer = updatedCustomers.find(item => item.id === customer.id) || customer;
      setCustomers(updatedCustomers);
      setStats(statsRes.data);
      setSelectedCustomer(updatedCustomer);
      setCustomerCard(cardRes.data);
      setCustomerHistory(historyRes.data || []);
      toast.success(`Fidelity Card di ${updatedCustomer.full_name} aggiornata`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l’aggiornamento della Fidelity Card');
    } finally {
      setRefreshingCustomerId(null);
    }
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setNewCustomerData({ full_name: '', email: '', phone: '', address: '', initial_points: '', admin_managed_only: false });
    setCreatedCustomerInfo(null);
  };

  const closeResetPasswordModal = () => {
    setShowResetPasswordModal(false);
    setResetPasswordResult(null);
  };

  const filteredCustomers = customers.filter(c => {
    const query = searchQuery.toLowerCase();
    return c.full_name?.toLowerCase().includes(query) ||
      c.email?.toLowerCase().includes(query) ||
      c.phone?.toLowerCase().includes(query) ||
      c.internal_customer_code?.toLowerCase().includes(query) ||
      (c.no_email_account && 'accesso senza email'.includes(query));
  });

  const { isDark } = useTheme();

  // Theme classes
  const t = {
    bg: isDark ? 'bg-gradient-to-br from-[#0a0a0a] via-[#1a1a2e] to-[#16213e]' : 'bg-gradient-to-br from-[#F9F7F2] via-[#FFFFFF] to-[#F5F3EE]',
    card: isDark ? 'bg-white/5 backdrop-blur-xl border-white/10' : 'bg-white shadow-lg border-gray-200',
    cardHover: isDark ? 'hover:bg-white/10' : 'hover:bg-gray-50',
    text: isDark ? 'text-white' : 'text-gray-900',
    textSecondary: isDark ? 'text-white/60' : 'text-gray-600',
    textMuted: isDark ? 'text-white/40' : 'text-gray-400',
    input: isDark ? 'bg-white/5 border-white/10 text-white placeholder-white/30' : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400',
    header: isDark ? 'bg-black/20 backdrop-blur-xl border-white/10' : 'bg-white/80 backdrop-blur-xl border-gray-200',
    navLink: isDark ? 'text-white/70 hover:text-white hover:bg-white/10' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100',
    modal: isDark ? 'bg-gradient-to-br from-[#1a1a2e] to-[#16213e] border-white/10' : 'bg-white border-gray-200 shadow-2xl',
    glow1: isDark ? 'bg-[#C5A059]/10' : 'bg-[#C5A059]/5',
    glow2: isDark ? 'bg-[#5D1822]/20' : 'bg-[#5D1822]/5',
  };

  if (loading) {
    if (user?.role === 'employee' && (user.permissions || []).length === 0) {
    return <div className="min-h-screen flex items-center justify-center p-6"><div className="bg-white rounded-2xl p-6 text-center"><h1 className="text-xl font-bold">Nessun permesso assegnato</h1><p className="mt-2">Chiedi all’amministratore di configurare il tuo ruolo.</p><button onClick={onLogout} className="mt-4 px-5 py-3 rounded-xl bg-red-700 text-white">Esci</button></div></div>;
  }
  return (
      <div className={`min-h-screen ${t.bg} flex items-center justify-center`}>
        <div className={`${t.text} text-xl flex items-center gap-3`}>
          <div className="w-8 h-8 border-4 border-[#C5A059] border-t-transparent rounded-full animate-spin"></div>
          Caricamento...
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${t.bg}`}>
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className={`absolute top-20 left-20 w-72 h-72 ${t.glow1} rounded-full blur-3xl animate-pulse`}></div>
        <div className={`absolute bottom-20 right-20 w-96 h-96 ${t.glow2} rounded-full blur-3xl animate-pulse`} style={{ animationDelay: '1s' }}></div>
      </div>

      {/* Header */}
      <header className={`relative z-10 border-b ${t.header}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#5D1822] to-[#3A0F15] flex items-center justify-center shadow-lg shadow-[#5D1822]/20">
                <Sparkles className="w-6 h-6 text-[#C5A059]" />
              </div>
              <div>
                <h1 className={`text-2xl font-bold ${t.text}`}>Macelleria Borgese</h1>
                <p className="text-sm text-[#C5A059]">Pannello Amministrazione</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className={`${t.textSecondary} text-sm hidden sm:block`}>{user?.full_name}</span>
              <ThemeToggle />
              <button onClick={onLogout} className={`p-2 rounded-lg ${t.textSecondary} ${t.cardHover} transition-colors`}>
                <LogOut size={20} />
              </button>
            </div>
          </div>

          {/* Menu amministratore compatto */}
          <div className="sm:hidden">
            <button
              type="button"
              onClick={() => setShowAdminMenu(previous => !previous)}
              className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border ${
                isDark ? 'border-white/10 bg-white/5 text-white' : 'border-gray-200 bg-white text-gray-900'
              }`}
            >
              <span className="flex items-center gap-2 font-semibold">
                <Menu size={19} />
                Menu amministratore
              </span>
              {showAdminMenu ? <XCircle size={19} /> : <ChevronRight size={19} className="rotate-90" />}
            </button>

            {showAdminMenu && (
              <div className={`grid grid-cols-2 gap-2 mt-3 p-3 rounded-2xl border ${
                isDark ? 'border-white/10 bg-black/20' : 'border-gray-200 bg-white'
              }`}>
                {[
                  { to: '/admin/orders', label: 'Ordini', icon: ShoppingBag },
                  { to: '/ordina', label: 'Pagina Ordina', icon: Store },
                  { to: '/admin/offers', label: 'Offerte', icon: Gift },
                  { to: '/admin/settings', label: 'Impostazioni', icon: SlidersHorizontal },
                  { to: '/admin/scanner-cassa', label: 'Scanner', icon: Zap },
                  { to: '/admin/physical-cards', label: 'Carte', icon: CreditCard },
                  { to: '/admin/points-history', label: 'Storico punti', icon: History }
                ].map(item => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={() => setShowAdminMenu(false)}
                      className={`min-h-[72px] rounded-xl border p-3 flex flex-col items-center justify-center gap-2 text-center font-semibold text-sm ${
                        isDark
                          ? 'border-white/10 bg-white/5 text-white hover:bg-white/10'
                          : 'border-gray-200 bg-gray-50 text-gray-900 hover:bg-gray-100'
                      }`}
                    >
                      <Icon size={22} className="text-[#C5A059]" />
                      {item.label}
                    </Link>
                  );
                })}

                <button
                  type="button"
                  onClick={handleExportCustomerPoints}
                  className={`col-span-2 min-h-[48px] rounded-xl border px-4 flex items-center justify-center gap-2 font-semibold text-sm ${
                    isDark
                      ? 'border-[#C5A059]/30 bg-[#C5A059]/10 text-[#E8C979]'
                      : 'border-[#C5A059]/40 bg-[#C5A059]/10 text-[#6B4F17]'
                  }`}
                >
                  <Download size={18} />
                  Scarica PDF punti clienti
                </button>

                <button
                  type="button"
                  onClick={handleSendCustomerPointsToTelegram}
                  disabled={sendingPointsReport}
                  className={`col-span-2 min-h-[48px] rounded-xl border px-4 flex items-center justify-center gap-2 font-semibold text-sm disabled:opacity-50 ${
                    isDark
                      ? 'border-sky-400/30 bg-sky-400/10 text-sky-300'
                      : 'border-sky-500/30 bg-sky-50 text-sky-700'
                  }`}
                >
                  <Send size={18} className={sendingPointsReport ? 'animate-pulse' : ''} />
                  {sendingPointsReport ? 'Invio PDF...' : 'Invia PDF punti su Telegram'}
                </button>

                <button
                  type="button"
                  onClick={handleExportData}
                  className={`col-span-2 min-h-[48px] rounded-xl border px-4 flex items-center justify-center gap-2 font-semibold text-sm ${
                    isDark
                      ? 'border-white/10 bg-white/5 text-white'
                      : 'border-gray-200 bg-gray-50 text-gray-900'
                  }`}
                >
                  <Download size={18} />
                  Scarica backup dati
                </button>
              </div>
            )}
          </div>

          <div className="hidden sm:flex items-center gap-2 overflow-x-auto pb-2">
            <Link
              to="/admin/orders"
              className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-[#5D1822] to-[#3A0F15] text-white rounded-xl font-semibold whitespace-nowrap"
            >
              <ShoppingBag size={18} />
              Ordini
            </Link>
            <Link to="/ordina" className={`flex items-center gap-2 px-4 py-2 rounded-lg ${t.navLink} transition-all whitespace-nowrap text-sm`}>
              <Store size={17} />
              Pagina Ordina
            </Link>
            <Link to="/admin/offers" className={`flex items-center gap-2 px-4 py-2 rounded-lg ${t.navLink} transition-all whitespace-nowrap text-sm`}>
              <Gift size={17} />
              Offerte
            </Link>
            <Link to="/admin/settings" className={`flex items-center gap-2 px-4 py-2 rounded-lg ${t.navLink} transition-all whitespace-nowrap text-sm`}>
              <Settings size={17} />
              Impostazioni
            </Link>
            <Link to="/admin/scanner-cassa" className={`flex items-center gap-2 px-4 py-2 rounded-lg ${t.navLink} transition-all whitespace-nowrap text-sm`}>
              <Zap size={17} />
              Scanner
            </Link>
            <Link to="/admin/physical-cards" className={`flex items-center gap-2 px-4 py-2 rounded-lg ${t.navLink} transition-all whitespace-nowrap text-sm`}>
              <CreditCard size={17} />
              Carte
            </Link>
            <button
              type="button"
              onClick={handleExportCustomerPoints}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg ${t.navLink} transition-all whitespace-nowrap text-sm ml-auto`}
              title="Scarica il PDF ordinato con i punti di tutti i clienti"
            >
              <Download size={17} />
              PDF punti
            </button>
            <button
              type="button"
              onClick={handleSendCustomerPointsToTelegram}
              disabled={sendingPointsReport}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg ${t.navLink} transition-all whitespace-nowrap text-sm disabled:opacity-50`}
              title="Invia subito su Telegram il PDF aggiornato dei punti"
            >
              <Send size={17} className={sendingPointsReport ? 'animate-pulse' : ''} />
              {sendingPointsReport ? 'Invio...' : 'Invia Telegram'}
            </button>
            <button
              type="button"
              onClick={handleExportData}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg ${t.navLink} transition-all whitespace-nowrap text-sm`}
            >
              <Download size={17} />
              Backup
            </button>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className={`rounded-2xl ${t.card} border p-6`}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-[#C5A059]/20 flex items-center justify-center">
                <Users className="w-5 h-5 text-[#C5A059]" />
              </div>
              <span className={`${t.textSecondary} text-sm`}>Clienti</span>
            </div>
            <p className={`text-3xl font-bold ${t.text}`}>{stats?.total_customers || 0}</p>
          </div>
          <div className={`rounded-2xl ${t.card} border p-6`}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-[#4A5D23]/20 flex items-center justify-center">
                <Coins className="w-5 h-5 text-[#7CB342]" />
              </div>
              <span className={`${t.textSecondary} text-sm`}>Punti Totali</span>
            </div>
            <p className="text-3xl font-bold text-[#7CB342]">{stats?.total_points || 0}</p>
          </div>
          <div className={`rounded-2xl ${t.card} border p-6`}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-[#5D1822]/20 flex items-center justify-center">
                <Gift className="w-5 h-5 text-[#C5A059]" />
              </div>
              <span className={`${t.textSecondary} text-sm`}>Sconti Usati</span>
            </div>
            <p className="text-3xl font-bold text-[#C5A059]">{stats?.total_discounts_used || 0}</p>
          </div>
          <div className={`rounded-2xl ${t.card} border p-6`}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-[#FF9800]/20 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-[#FF9800]" />
              </div>
              <span className={`${t.textSecondary} text-sm`}>Transazioni</span>
            </div>
            <p className={`text-3xl font-bold ${t.text}`}>{stats?.total_transactions || 0}</p>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Customers List */}
          <div className={`rounded-2xl ${t.card} border overflow-hidden`}>
            <div className={`p-6 border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <h2 className={`text-xl font-bold ${t.text}`}>Clienti</h2>
                  <button
                    onClick={() => { fetchData(); toast.success('Aggiornato!'); }}
                    className={`p-2 ${t.textMuted} ${t.cardHover} rounded-lg transition-colors`}
                  >
                    <RefreshCw size={16} />
                  </button>
                </div>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#4A5D23] to-[#2E3A16] text-white rounded-lg text-sm font-medium hover:shadow-lg hover:shadow-[#4A5D23]/30 transition-all"
                >
                  <Plus size={16} />
                  Nuovo
                </button>
              </div>
              <div className="relative">
                <Search className={`absolute left-3 top-1/2 -translate-y-1/2 ${t.textMuted} w-5 h-5`} />
                <input
                  type="text"
                  placeholder="Cerca cliente..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`w-full pl-10 pr-4 py-3 ${t.input} border rounded-xl focus:outline-none focus:border-[#C5A059]/50`}
                />
              </div>
            </div>
            
            <div className="max-h-[500px] overflow-y-auto">
              {filteredCustomers.length === 0 ? (
                <div className={`p-8 text-center ${t.textMuted}`}>
                  Nessun cliente trovato
                </div>
              ) : (
                filteredCustomers.map(customer => (
                  <div
                    key={customer.id}
                    className={`w-full flex items-stretch border-b ${isDark ? 'border-white/5 hover:bg-white/5' : 'border-gray-100 hover:bg-gray-50'} transition-colors ${
                      selectedCustomer?.id === customer.id ? 'bg-[#C5A059]/10 border-l-2 border-l-[#C5A059]' : ''
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => handleSelectCustomer(customer)}
                      className="flex-1 min-w-0 p-4 pr-2 flex items-center gap-4 text-left"
                    >
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#5D1822] to-[#3A0F15] flex items-center justify-center text-white font-bold flex-shrink-0">
                        {customer.full_name?.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`${t.text} font-medium truncate`}>{customer.full_name}</p>
                        <p className={`${t.textSecondary} text-sm truncate`}>
                          {customer.no_email_account
                            ? 'Senza email · accesso con nome e telefono'
                            : customer.admin_managed_only
                              ? 'Senza email · accesso da attivare'
                              : customer.email}
                        </p>
                        {(customer.no_email_account || customer.admin_managed_only) && customer.phone && (
                          <p className={`${t.textMuted} text-xs truncate`}>{customer.phone}</p>
                        )}
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={(event) => handleRefreshCustomerCard(customer, event)}
                      disabled={Boolean(refreshingCustomerId)}
                      className={`m-3 ml-1 px-3 rounded-xl flex flex-col items-center justify-center gap-1 text-[11px] font-semibold transition-colors disabled:opacity-50 ${
                        isDark
                          ? 'bg-white/5 text-[#C5A059] hover:bg-white/10'
                          : 'bg-gray-100 text-[#7A5C1B] hover:bg-gray-200'
                      }`}
                      title={`Aggiorna la Fidelity Card di ${customer.full_name}`}
                      aria-label={`Aggiorna la Fidelity Card di ${customer.full_name}`}
                    >
                      <RefreshCw size={17} className={refreshingCustomerId === customer.id ? 'animate-spin' : ''} />
                      <span>Aggiorna</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSelectCustomer(customer)}
                      className={`px-2 ${t.textMuted}`}
                      aria-label={`Apri la Fidelity Card di ${customer.full_name}`}
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Customer Details */}
          <div className={`rounded-2xl ${t.card} border overflow-hidden`}>
            {selectedCustomer ? (
              <>
                <div className={`p-6 border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#C5A059] to-[#8B6914] flex items-center justify-center text-white text-2xl font-bold">
                        {selectedCustomer.full_name?.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <h3 className={`text-xl font-bold ${t.text}`}>{selectedCustomer.full_name}</h3>
                        <p className={`${t.textSecondary} text-sm`}>
                          {selectedCustomer.no_email_account
                            ? 'Cliente senza email · accesso con nome e telefono'
                            : selectedCustomer.admin_managed_only
                              ? 'Cliente senza email · accesso da attivare'
                              : selectedCustomer.email}
                        </p>
                        {selectedCustomer.phone && <p className={`${t.textMuted} text-xs mt-1`}>{selectedCustomer.phone}</p>}
                        {selectedCustomer.blocked && (
                          <span className="inline-flex items-center gap-1 mt-2 px-2 py-1 rounded-full bg-red-500/15 text-red-500 text-xs font-semibold">
                            <XCircle size={13} /> Email bloccata
                          </span>
                        )}
                        {(selectedCustomer.no_email_account || selectedCustomer.admin_managed_only) && selectedCustomer.internal_customer_code && (
                          <p className="text-[#C5A059] text-xs mt-1">Codice: {selectedCustomer.internal_customer_code}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => setShowViewCardModal(true)} className={`p-2 rounded-lg ${isDark ? 'bg-white/5 text-white/60 hover:text-[#C5A059] hover:bg-white/10' : 'bg-gray-100 text-gray-500 hover:text-[#C5A059] hover:bg-gray-200'} transition-all`} title="Vedi Carta">
                        <QrCodeIcon size={18} />
                      </button>
                      <button onClick={() => setShowResetPasswordModal(true)} className={`p-2 rounded-lg ${isDark ? 'bg-white/5 text-white/60 hover:text-blue-400 hover:bg-white/10' : 'bg-gray-100 text-gray-500 hover:text-blue-500 hover:bg-gray-200'} transition-all`} title={selectedCustomer.admin_managed_only ? 'Attiva accesso cliente' : 'Reset Password'}>
                        <Key size={18} />
                      </button>
                      <button onClick={() => setShowResetPointsModal(true)} className={`p-2 rounded-lg ${isDark ? 'bg-white/5 text-white/60 hover:text-amber-400 hover:bg-white/10' : 'bg-gray-100 text-gray-500 hover:text-amber-600 hover:bg-gray-200'} transition-all`} title="Azzera punti">
                        <Coins size={18} />
                      </button>
                      {user?.role === 'admin' && !selectedCustomer.no_email_account && !selectedCustomer.admin_managed_only && selectedCustomer.email && (
                        <button
                          onClick={() => setShowEmailBlockModal(true)}
                          className={`p-2 rounded-lg transition-all ${
                            selectedCustomer.blocked
                              ? (isDark ? 'bg-green-500/15 text-green-400 hover:bg-green-500/25' : 'bg-green-50 text-green-600 hover:bg-green-100')
                              : (isDark ? 'bg-red-500/10 text-red-400 hover:bg-red-500/20' : 'bg-red-50 text-red-600 hover:bg-red-100')
                          }`}
                          title={selectedCustomer.blocked ? 'Sblocca email' : 'Blocca email'}
                          aria-label={selectedCustomer.blocked ? `Sblocca ${selectedCustomer.email}` : `Blocca ${selectedCustomer.email}`}
                        >
                          {selectedCustomer.blocked ? <RefreshCw size={18} /> : <XCircle size={18} />}
                        </button>
                      )}
                      <button onClick={() => setShowDeleteModal(true)} className={`p-2 rounded-lg ${isDark ? 'bg-white/5 text-white/60 hover:text-red-400 hover:bg-white/10' : 'bg-gray-100 text-gray-500 hover:text-red-500 hover:bg-gray-200'} transition-all`} title="Elimina">
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                </div>

                {customerCard && (
                  <div className="p-6">
                    {/* Points Card */}
                    <div className="rounded-xl bg-gradient-to-br from-[#5D1822] to-[#3A0F15] p-6 mb-6">
                      <div className="flex justify-between items-start mb-6">
                        <div>
                          <p className="text-[#C5A059] text-sm mb-1">Punti Attuali</p>
                          <p className="text-5xl font-bold text-white">{customerCard.current_points}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-white/60 text-sm mb-1">Totale Guadagnati</p>
                          <p className="text-2xl font-bold text-white">{customerCard.total_points_earned}</p>
                        </div>
                      </div>
                      
                      {/* Progress */}
                      <div className="mb-4">
                        <div className="flex justify-between text-sm mb-2">
                          <span className="text-white/60">Progresso sconto</span>
                          <span className="text-[#C5A059]">{Math.min(Number(customerCard.current_points || 0), Number(loyaltySettings?.points_for_discount || 10))}/{Number(loyaltySettings?.points_for_discount || 10)}</span>
                        </div>
                        <div className="w-full bg-white/20 rounded-full h-2 overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-[#C5A059] to-[#FFD700] transition-all duration-500"
                            style={{ width: `${Math.min(100, (Number(customerCard.current_points || 0) / Number(loyaltySettings?.points_for_discount || 10)) * 100)}%` }}
                          ></div>
                        </div>
                      </div>
                      
                      <p className="text-white/50 text-sm text-center">
                        {Number(customerCard.current_points || 0) >= Number(loyaltySettings?.points_for_discount || 10)
                          ? `Sconto disponibile: ${Number(loyaltySettings?.points_for_discount || 10)} punti = €${Number(loyaltySettings?.discount_value || 5).toFixed(2)}`
                          : `${Number(loyaltySettings?.points_for_discount || 10) - Number(customerCard.current_points || 0)} punti al prossimo sconto da €${Number(loyaltySettings?.discount_value || 5).toFixed(2)}`}
                      </p>
                    </div>

                    {/* Action Button */}
                    <button
                      onClick={() => setShowPurchaseModal(true)}
                      className="w-full py-4 bg-gradient-to-r from-[#4A5D23] to-[#2E3A16] text-white rounded-xl font-semibold flex items-center justify-center gap-3 hover:shadow-lg hover:shadow-[#4A5D23]/30 transition-all"
                    >
                      <Plus size={20} />
                      Registra Acquisto
                    </button>

                    <div className="mt-3">
                      <button
                        onClick={handleRedeemStandardDiscount}
                        disabled={redeemPointsLoading || Number(customerCard.current_points || 0) < Number(loyaltySettings?.points_for_discount || 10)}
                        className="w-full py-4 bg-gradient-to-r from-[#C5A059] to-[#9A783C] text-white rounded-xl font-semibold flex items-center justify-center gap-3 hover:shadow-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <Gift size={20} />
                        {redeemPointsLoading ? 'Applicazione sconto...' : 'Sconta punti'}
                      </button>
                      <p className={`mt-2 text-center text-sm font-semibold ${t.textSecondary}`}>
                        {Number(loyaltySettings?.points_for_discount || 10)} punti = €{Number(loyaltySettings?.discount_value || 5).toFixed(2)} di sconto
                      </p>
                    </div>

                    {/* Purchase and points history for the selected customer */}
                    <div className={`mt-6 rounded-xl border ${isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-gray-50'} overflow-hidden`}>
                      <div className={`px-4 py-3 border-b ${isDark ? 'border-white/10' : 'border-gray-200'} flex items-center justify-between`}>
                        <div className="flex items-center gap-2">
                          <History size={18} className="text-[#C5A059]" />
                          <h4 className={`font-semibold ${t.text}`}>Cronologia punti e acquisti</h4>
                        </div>
                        <span className={`text-xs ${t.textMuted}`}>{customerHistory.length} movimenti</span>
                      </div>

                      <div className="max-h-72 overflow-y-auto">
                        {historyLoading ? (
                          <div className={`p-5 text-center text-sm ${t.textSecondary}`}>Caricamento cronologia...</div>
                        ) : customerHistory.length === 0 ? (
                          <div className={`p-5 text-center text-sm ${t.textSecondary}`}>Nessun movimento registrato</div>
                        ) : (
                          customerHistory.map((item) => {
                            const isPurchase = item.type === 'purchase';
                            const isPositive = Number(item.points) >= 0;
                            return (
                              <div key={item.id} className={`px-4 py-3 border-b last:border-b-0 ${isDark ? 'border-white/5' : 'border-gray-200'}`}>
                                <div className="flex items-start justify-between gap-3">
                                  <div className="min-w-0">
                                    <p className={`font-medium ${t.text} truncate`}>{item.description || (isPurchase ? 'Acquisto' : 'Movimento punti')}</p>
                                    <p className={`text-xs mt-1 ${t.textMuted}`}>
                                      {item.created_at ? new Date(item.created_at).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' }) : ''}
                                    </p>
                                    {isPurchase && (
                                      <p className={`text-sm mt-1 ${t.textSecondary}`}>Spesa registrata: €{Number(item.amount || 0).toFixed(2)}</p>
                                    )}
                                  </div>
                                  <div className="text-right flex-shrink-0">
                                    <p className={`font-bold ${isPositive ? 'text-[#7CB342]' : 'text-red-500'}`}>
                                      {isPositive ? '+' : ''}{item.points} punti
                                    </p>
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="p-12 text-center">
                <div className={`w-20 h-20 mx-auto mb-6 rounded-full ${isDark ? 'bg-white/5' : 'bg-gray-100'} flex items-center justify-center`}>
                  <User className={`w-10 h-10 ${t.textMuted}`} />
                </div>
                <p className={`${t.textMuted} text-lg`}>Seleziona un cliente</p>
                <p className={`${t.textMuted} text-sm mt-2`}>Clicca su un cliente per vedere i dettagli</p>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Purchase Modal */}
      {showPurchaseModal && selectedCustomer && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`${t.modal} rounded-3xl max-w-md w-full p-8 border`}>
            <h3 className={`text-2xl font-bold ${t.text} mb-2`}>Registra Acquisto</h3>
            <p className={`${t.textSecondary} mb-6`}>{selectedCustomer.full_name}</p>
            
            <div className="mb-6">
              <label className={`${t.textSecondary} text-sm mb-2 block`}>Importo Spesa</label>
              <div className="relative">
                <Euro className="absolute left-4 top-1/2 -translate-y-1/2 text-[#C5A059] w-6 h-6" />
                <input
                  type="number"
                  value={purchaseAmount}
                  onChange={(e) => setPurchaseAmount(e.target.value)}
                  placeholder="0.00"
                  className={`w-full pl-14 pr-4 py-4 ${t.input} border rounded-xl text-2xl font-bold focus:outline-none focus:border-[#4A5D23]/50`}
                  step="0.01"
                  autoFocus
                />
              </div>
            </div>
            
            <div className="flex gap-3">
              <button onClick={() => { setShowPurchaseModal(false); setPurchaseAmount(''); }} className="flex-1 py-3 bg-white/10 text-white rounded-xl font-medium hover:bg-white/20">
                Annulla
              </button>
              <button onClick={handleAddPurchase} disabled={processing || !purchaseAmount} className="flex-1 py-3 bg-gradient-to-r from-[#4A5D23] to-[#2E3A16] text-white rounded-xl font-semibold disabled:opacity-50">
                {processing ? 'Elaborazione...' : 'Conferma'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Customer Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-[#1a1a2e] to-[#16213e] rounded-3xl max-w-md w-full p-8 border border-white/10 max-h-[92vh] overflow-y-auto">
            {!createdCustomerInfo ? (
              <>
                <h3 className="text-2xl font-bold text-white mb-2">Nuovo Cliente</h3>
                <p className="text-white/50 text-sm mb-6">Puoi registrare anche chi non possiede un'email.</p>

                <label className="flex items-start gap-3 p-4 mb-5 rounded-xl border border-[#C5A059]/30 bg-[#C5A059]/10 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newCustomerData.admin_managed_only}
                    onChange={(e) => setNewCustomerData({
                      ...newCustomerData,
                      admin_managed_only: e.target.checked,
                      email: e.target.checked ? '' : newCustomerData.email,
                    })}
                    className="mt-1 w-5 h-5 accent-[#C5A059]"
                  />
                  <span>
                    <span className="block text-white font-semibold">Cliente senza email</span>
                    <span className="block text-white/60 text-xs mt-1">Accederà con nome, numero di telefono e password casuale.</span>
                  </span>
                </label>

                <div className="space-y-4">
                  <input type="text" placeholder="Nome Completo *" value={newCustomerData.full_name} onChange={(e) => setNewCustomerData({...newCustomerData, full_name: e.target.value})} className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-[#C5A059]/50" />
                  {!newCustomerData.admin_managed_only && (
                    <input type="email" placeholder="Email *" value={newCustomerData.email} onChange={(e) => setNewCustomerData({...newCustomerData, email: e.target.value})} className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-[#C5A059]/50" />
                  )}
                  <input type="tel" placeholder={newCustomerData.admin_managed_only ? 'Telefono *' : 'Telefono'} value={newCustomerData.phone} onChange={(e) => setNewCustomerData({...newCustomerData, phone: e.target.value})} className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-[#C5A059]/50" />
                  <input type="text" placeholder="Indirizzo (facoltativo)" value={newCustomerData.address} onChange={(e) => setNewCustomerData({...newCustomerData, address: e.target.value})} className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-[#C5A059]/50" />
                  <div>
                    <label className="block text-white/60 text-sm mb-2">Punti iniziali (facoltativi)</label>
                    <input type="number" min="0" step="1" placeholder="0" value={newCustomerData.initial_points} onChange={(e) => setNewCustomerData({...newCustomerData, initial_points: e.target.value})} className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-[#C5A059]/50" />
                  </div>
                </div>
                <div className="flex gap-3 mt-6">
                  <button onClick={closeCreateModal} className="flex-1 py-3 bg-white/10 text-white rounded-xl font-medium hover:bg-white/20">Annulla</button>
                  <button onClick={handleCreateCustomer} disabled={processing} className="flex-1 py-3 bg-gradient-to-r from-[#4A5D23] to-[#2E3A16] text-white rounded-xl font-semibold disabled:opacity-50">
                    {processing ? 'Creazione...' : 'Crea Cliente'}
                  </button>
                </div>
              </>
            ) : (
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#4A5D23]/30 flex items-center justify-center">
                  <Award className="w-8 h-8 text-[#7CB342]" />
                </div>
                <h3 className="text-xl font-bold text-[#7CB342] mb-4">Cliente Creato!</h3>
                <div className="bg-white/10 rounded-xl p-4 mb-4 text-left space-y-2">
                  <p className="text-white/60 text-sm">Nome: <span className="text-white">{createdCustomerInfo.customer?.full_name}</span></p>
                  {createdCustomerInfo.customer?.phone && <p className="text-white/60 text-sm">Telefono: <span className="text-white">{createdCustomerInfo.customer.phone}</span></p>}
                  {createdCustomerInfo.customer?.no_email_account && (
                    <p className="text-white/60 text-sm">Accesso: <span className="text-[#C5A059] font-semibold">Nome + telefono</span></p>
                  )}
                  {createdCustomerInfo.customer?.internal_customer_code && (
                    <p className="text-white/60 text-sm">Codice: <span className="text-[#C5A059] font-mono font-bold">{createdCustomerInfo.customer.internal_customer_code}</span></p>
                  )}
                  <p className="text-white/60 text-sm">Password: <span className="text-[#C5A059] font-mono font-bold">{createdCustomerInfo.temporary_password}</span></p>
                  {Number(createdCustomerInfo.initial_points || 0) > 0 && <p className="text-white/60 text-sm">Punti iniziali: <span className="text-[#7CB342] font-bold">{createdCustomerInfo.initial_points}</span></p>}
                </div>
                <p className="text-[#C5A059] text-sm mb-6">
                  {createdCustomerInfo.customer?.no_email_account
                    ? '⚠️ Comunica al cliente nome, telefono e password mostrata sopra.'
                    : '⚠️ Comunica la password al cliente'}
                </p>
                <button onClick={closeCreateModal} className="w-full py-3 bg-gradient-to-r from-[#4A5D23] to-[#2E3A16] text-white rounded-xl font-semibold">Chiudi</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPasswordModal && selectedCustomer && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-[#1a1a2e] to-[#16213e] rounded-3xl max-w-md w-full p-8 border border-white/10">
            {!resetPasswordResult ? (
              <>
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-500/20 flex items-center justify-center">
                  <Key className="w-8 h-8 text-blue-400" />
                </div>
                <h3 className="text-xl font-bold text-white text-center mb-2">Reset Password</h3>
                <p className="text-white/60 text-center mb-6">Resettare la password di <strong className="text-white">{selectedCustomer.full_name}</strong>?</p>
                <div className="flex gap-3">
                  <button onClick={closeResetPasswordModal} className="flex-1 py-3 bg-white/10 text-white rounded-xl font-medium hover:bg-white/20">Annulla</button>
                  <button onClick={handleResetPassword} disabled={resetPasswordLoading} className="flex-1 py-3 bg-blue-500 text-white rounded-xl font-semibold disabled:opacity-50">
                    {resetPasswordLoading ? 'Elaborazione...' : 'Resetta'}
                  </button>
                </div>
              </>
            ) : (
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#4A5D23]/30 flex items-center justify-center">
                  <Key className="w-8 h-8 text-[#7CB342]" />
                </div>
                <h3 className="text-xl font-bold text-[#7CB342] mb-4">Password Resettata!</h3>
                <div className="bg-white/10 rounded-xl p-4 mb-4">
                  <p className="text-white/60 text-sm">Nuova password:</p>
                  <p className="text-2xl font-mono font-bold text-[#C5A059]">{resetPasswordResult.temporary_password}</p>
                </div>
                {resetPasswordResult.access_activated && (
                  <div className="bg-white/10 rounded-xl p-3 mb-4 text-left text-sm">
                    <p className="text-white/60">Accesso con nome: <span className="text-white">{resetPasswordResult.login_full_name}</span></p>
                    <p className="text-white/60">Telefono: <span className="text-white">{resetPasswordResult.login_phone}</span></p>
                  </div>
                )}
                <p className="text-[#C5A059] text-sm mb-6">⚠️ Comunica le credenziali al cliente</p>
                <button onClick={closeResetPasswordModal} className="w-full py-3 bg-gradient-to-r from-[#4A5D23] to-[#2E3A16] text-white rounded-xl font-semibold">Chiudi</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Reset Customer Points Modal */}
      {showResetPointsModal && selectedCustomer && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`${t.modal} rounded-3xl max-w-md w-full p-8 border`}>
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-500/20 flex items-center justify-center">
              <Coins className="w-8 h-8 text-amber-500" />
            </div>
            <h3 className={`text-xl font-bold ${t.text} text-center mb-2`}>Azzera punti cliente</h3>
            <p className={`${t.textSecondary} text-center mb-6`}>
              Stai per azzerare i punti correnti di <strong className={t.text}>{selectedCustomer.full_name}</strong>.
            </p>
            <label className={`block text-sm font-medium ${t.text} mb-2`}>Password amministratore</label>
            <input
              type="password"
              value={resetPointsPassword}
              onChange={(e) => setResetPointsPassword(e.target.value)}
              className={`w-full px-4 py-3 ${t.input} border rounded-xl focus:outline-none focus:border-amber-500/60 mb-6`}
              placeholder="Inserisci la password"
              autoFocus
            />
            <div className="flex gap-3">
              <button
                onClick={() => { setShowResetPointsModal(false); setResetPointsPassword(''); }}
                className={`flex-1 py-3 rounded-xl font-medium ${isDark ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                Annulla
              </button>
              <button
                onClick={handleResetCustomerPoints}
                disabled={resetPointsLoading || !resetPointsPassword}
                className="flex-1 py-3 bg-amber-600 text-white rounded-xl font-semibold disabled:opacity-50"
              >
                {resetPointsLoading ? 'Azzeramento...' : 'Azzera punti'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Block / Unblock Email Modal */}
      {showEmailBlockModal && selectedCustomer && selectedCustomer.email && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`${t.modal} rounded-3xl max-w-md w-full p-8 border`}>
            <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${selectedCustomer.blocked ? 'bg-green-500/15' : 'bg-red-500/15'}`}>
              {selectedCustomer.blocked
                ? <RefreshCw className="w-8 h-8 text-green-500" />
                : <XCircle className="w-8 h-8 text-red-500" />}
            </div>
            <h3 className={`text-xl font-bold ${t.text} text-center mb-2`}>
              {selectedCustomer.blocked ? 'Sblocca email' : 'Blocca email'}
            </h3>
            <p className={`${t.textSecondary} text-center mb-2`}>
              <strong className={t.text}>{selectedCustomer.email}</strong>
            </p>
            <p className={`${t.textSecondary} text-sm text-center mb-6`}>
              {selectedCustomer.blocked
                ? 'Il cliente potrà nuovamente accedere, recuperare la password e utilizzare questa email.'
                : 'Il cliente non potrà accedere, registrarsi nuovamente o recuperare la password con questa email. I suoi dati e punti resteranno conservati.'}
            </p>

            {!selectedCustomer.blocked && (
              <div className="mb-4">
                <label className={`block text-sm font-medium ${t.text} mb-2`}>Motivo (facoltativo)</label>
                <textarea
                  value={emailBlockReason}
                  onChange={(event) => setEmailBlockReason(event.target.value)}
                  maxLength={500}
                  rows={3}
                  className={`w-full px-4 py-3 ${t.input} border rounded-xl focus:outline-none focus:border-red-500/60 resize-none`}
                  placeholder="Esempio: account duplicato o utilizzo improprio"
                />
              </div>
            )}

            <label className={`block text-sm font-medium ${t.text} mb-2`}>Password amministratore</label>
            <input
              type="password"
              value={emailBlockPassword}
              onChange={(event) => setEmailBlockPassword(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && emailBlockPassword && !emailBlockLoading) handleToggleEmailBlock();
              }}
              className={`w-full px-4 py-3 ${t.input} border rounded-xl focus:outline-none ${selectedCustomer.blocked ? 'focus:border-green-500/60' : 'focus:border-red-500/60'} mb-6`}
              placeholder="Inserisci la password"
              autoFocus
            />

            <div className="flex gap-3">
              <button
                onClick={closeEmailBlockModal}
                disabled={emailBlockLoading}
                className={`flex-1 py-3 rounded-xl font-medium ${isDark ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} disabled:opacity-50`}
              >
                Annulla
              </button>
              <button
                onClick={handleToggleEmailBlock}
                disabled={emailBlockLoading || !emailBlockPassword}
                className={`flex-1 py-3 text-white rounded-xl font-semibold disabled:opacity-50 ${selectedCustomer.blocked ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}
              >
                {emailBlockLoading
                  ? 'Elaborazione...'
                  : selectedCustomer.blocked ? 'Sblocca email' : 'Blocca email'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Card Modal */}
      {showViewCardModal && selectedCustomer && customerCard && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-[#1a1a2e] to-[#16213e] rounded-3xl max-w-md w-full p-8 border border-white/10">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-white">Carta di {selectedCustomer.full_name}</h3>
              <button onClick={() => setShowViewCardModal(false)} className="text-white/60 hover:text-white">
                <X size={24} />
              </button>
            </div>
            
            {/* Card Preview */}
            <div className="rounded-xl bg-gradient-to-br from-[#5D1822] to-[#3A0F15] p-6 text-white mb-6">
              <p className="text-[#C5A059] text-sm mb-1">Macelleria Borgese</p>
              <p className="font-bold text-lg mb-4">{selectedCustomer.full_name}</p>
              <div className="flex justify-between">
                <div>
                  <p className="text-white/60 text-xs">Punti</p>
                  <p className="text-2xl font-bold">{customerCard.current_points}</p>
                </div>
                <div className="text-right">
                  <p className="text-white/60 text-xs">Totale</p>
                  <p className="text-xl font-bold">{customerCard.total_points_earned}</p>
                </div>
              </div>
            </div>
            
            {/* QR Code */}
            <div className="text-center">
              <p className="text-white/60 text-sm mb-3">QR Code</p>
              <div className="inline-block bg-white p-4 rounded-xl">
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(`BORGESE-CARD-${customerCard.card_id}`)}`}
                  alt="QR Code"
                  className="w-36 h-36"
                />
              </div>
              <p className="text-white/40 text-xs mt-2 font-mono">{customerCard.card_id?.slice(0, 8)}...</p>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && selectedCustomer && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-[#1a1a2e] to-[#16213e] rounded-3xl max-w-md w-full p-8 border border-red-500/30">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
              <Trash2 className="w-8 h-8 text-red-400" />
            </div>
            <h3 className="text-xl font-bold text-white text-center mb-2">Elimina Cliente</h3>
            <p className="text-white/60 text-center mb-6">Eliminare <strong className="text-red-400">{selectedCustomer.full_name}</strong>? Questa azione è irreversibile.</p>
            <div className="flex gap-3">
              <button onClick={() => setShowDeleteModal(false)} className="flex-1 py-3 bg-white/10 text-white rounded-xl font-medium hover:bg-white/20">Annulla</button>
              <button onClick={handleDeleteCustomer} className="flex-1 py-3 bg-red-500 text-white rounded-xl font-semibold hover:bg-red-600">Elimina</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
